class RoutineSelector extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.cards = Array.from(this.querySelectorAll('[data-routine-card]'));
    this.button = this.querySelector('[data-routine-submit]');
    this.status = this.querySelector('[data-routine-status]');

    this.cards.forEach((card) => {
      const checkbox = card.querySelector('[data-routine-check]');
      const select = card.querySelector('[data-routine-variant]');

      checkbox?.addEventListener('change', () => {
        card.classList.toggle('is-selected', checkbox.checked);
        this.updateButton();
      });

      select?.addEventListener('change', () => this.updateVariant(card, select));
      if (select) this.updateVariant(card, select);
    });

    this.button?.addEventListener('click', () => this.addSelected());

    // Horizon product templates are intercepted here. The custom Luscious PDP
    // includes selected routine items directly inside assets/luscious-pdp.js.
    const horizonComponent = this.getHorizonProductFormComponent();
    this.horizonProductForm = horizonComponent?.querySelector('form[data-type="add-to-cart-form"]') || null;
    this.horizonProductForm?.addEventListener('submit', this.handleHorizonMainFormSubmit, { capture: true });

    this.updateButton();
  }

  disconnectedCallback() {
    this.horizonProductForm?.removeEventListener('submit', this.handleHorizonMainFormSubmit, { capture: true });
  }

  updateVariant(card, select) {
    const isSelect = select instanceof HTMLSelectElement;
    const option = isSelect ? select.options[select.selectedIndex] : null;
    const source = option || select;
    const variantId = source?.value || select.value;
    if (!variantId) return;

    const price = card.querySelector('[data-routine-price]');
    const comparePrice = card.querySelector('[data-routine-compare]');
    const image = card.querySelector('[data-routine-image]');
    const checkbox = card.querySelector('[data-routine-check]');

    if (price && source?.dataset.price) price.textContent = source.dataset.price;
    if (comparePrice) {
      const compare = source?.dataset.comparePrice || '';
      comparePrice.textContent = compare;
      comparePrice.hidden = !compare;
    }
    if (image && source?.dataset.image) image.src = source.dataset.image;

    if (checkbox) {
      checkbox.dataset.variantId = variantId;
      const unavailable = Boolean(option?.disabled) || checkbox.disabled;
      if (unavailable) {
        checkbox.checked = false;
        card.classList.remove('is-selected');
      }
    }
    this.updateButton();
  }

  selectedItems() {
    return this.cards.map((card) => {
      const checkbox = card.querySelector('[data-routine-check]');
      const select = card.querySelector('[data-routine-variant]');
      if (!checkbox?.checked) return null;
      const id = Number(select?.value || checkbox.dataset.variantId);
      return id ? { id, quantity: 1 } : null;
    }).filter(Boolean);
  }

  getHorizonProductFormComponent() {
    const productId = this.dataset.mainProductId;
    if (!productId) return null;
    const forms = Array.from(document.querySelectorAll(`product-form-component[data-product-id="${CSS.escape(productId)}"]`));
    return forms.find((component) => !component.classList.contains('quick-add__product-form-component')) || forms[0] || null;
  }

  getLusciousProductForm() {
    return document.querySelector('#luscious-pdp-product-form') || document.querySelector('.luscious-pdp__form');
  }

  mainProductItem() {
    const horizon = this.getHorizonProductFormComponent();
    if (horizon) {
      const form = horizon.querySelector('form[data-type="add-to-cart-form"]');
      const variantInput = form?.querySelector('input[name="id"]');
      const quantityInput = form?.querySelector('[name="quantity"]');
      if (variantInput?.disabled) return null;
      const id = Number(variantInput?.value || this.dataset.mainVariantId);
      if (!id) return null;
      const requestedQuantity = Number(quantityInput?.value || this.dataset.mainQuantity || 1);
      return { id, quantity: Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? Math.floor(requestedQuantity) : 1 };
    }

    const form = this.getLusciousProductForm();
    const variantInput = form?.querySelector('[data-luscious-variant-id]') || form?.querySelector('input[name="id"]');
    const quantityInput = form?.querySelector('[data-luscious-quantity]') || form?.querySelector('[name="quantity"]');
    const id = Number(variantInput?.value || variantInput?.dataset?.variantId || this.dataset.mainVariantId);
    if (!id) return null;
    const requestedQuantity = Number(quantityInput?.value || this.dataset.mainQuantity || 1);
    return { id, quantity: Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? Math.floor(requestedQuantity) : 1 };
  }

  mergeItems(items) {
    const merged = new Map();
    items.forEach((item) => {
      if (!item?.id || !item?.quantity) return;
      merged.set(Number(item.id), (merged.get(Number(item.id)) || 0) + Number(item.quantity));
    });
    return Array.from(merged, ([id, quantity]) => ({ id, quantity }));
  }

  updateButton() {
    if (!this.button) return;
    const count = this.selectedItems().length;
    this.button.disabled = count === 0 || this.button.dataset.loading === 'true';
    const label = this.button.querySelector('[data-routine-button-label]');
    if (label) label.textContent = count ? `${this.button.dataset.label || 'SELECT ITEMS'} (${count})` : (this.button.dataset.label || 'SELECT ITEMS');
  }

  setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message || '';
    this.status.classList.toggle('is-error', isError);
  }

  clearRoutineSelection() {
    this.cards.forEach((card) => {
      const checkbox = card.querySelector('[data-routine-check]');
      if (checkbox) checkbox.checked = false;
      card.classList.remove('is-selected');
    });
    this.updateButton();
  }

  setHorizonLoading(isLoading) {
    const component = this.getHorizonProductFormComponent();
    if (!component) return;
    component.toggleAttribute('aria-busy', isLoading);
    component.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((button) => {
      if (isLoading) {
        button.dataset.routineWasDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
      } else {
        if (button.dataset.routineWasDisabled !== 'true') button.disabled = false;
        delete button.dataset.routineWasDisabled;
      }
    });
  }

  async addItems(items, { source = 'routine-selector', routineLoading = true, horizonLoading = false } = {}) {
    if (!items.length) return false;
    const label = this.button?.querySelector('[data-routine-button-label]');
    const originalLabel = label?.textContent || this.button?.dataset.label || 'SELECT ITEMS';

    if (routineLoading && this.button) {
      this.button.dataset.loading = 'true';
      this.button.disabled = true;
      this.classList.add('is-loading');
      if (label) label.textContent = this.button.dataset.loadingLabel || 'ADDING…';
    }
    if (horizonLoading) this.setHorizonLoading(true);
    this.setStatus('');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (!response.ok || data?.status) throw new Error(data?.description || data?.message || 'Unable to add the selected items.');

      this.clearRoutineSelection();
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: data, sourceId: this.id, data: { source } } }));
      document.body.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: data } }));
      this.setStatus(this.button?.dataset.successMessage || 'Selected items added to your cart.');
      return true;
    } catch (error) {
      console.error('[Routine selector]', error);
      this.setStatus(error?.message || 'Unable to add the selected items. Please try again.', true);
      return false;
    } finally {
      if (routineLoading && this.button) {
        this.button.dataset.loading = 'false';
        this.classList.remove('is-loading');
        if (label) label.textContent = originalLabel;
      }
      if (horizonLoading) this.setHorizonLoading(false);
      this.updateButton();
    }
  }

  async addSelected() {
    const routineItems = this.selectedItems();
    if (!routineItems.length) return;
    const mainItem = this.mainProductItem();
    if (!mainItem) {
      this.setStatus('Please select an available option for the main product.', true);
      return;
    }
    await this.addItems(this.mergeItems([mainItem, ...routineItems]), { source: 'routine-selector' });
  }

  handleHorizonMainFormSubmit = (event) => {
    const routineItems = this.selectedItems();
    if (!routineItems.length) return;
    const mainItem = this.mainProductItem();
    if (!mainItem) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setStatus('Please select an available option for the main product.', true);
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addItems(this.mergeItems([mainItem, ...routineItems]), {
      source: 'routine-selector-main-add',
      routineLoading: false,
      horizonLoading: true
    });
  };
}

if (!customElements.get('routine-selector')) customElements.define('routine-selector', RoutineSelector);
